import { createRef } from "react";
import { MessageSquare, BookOpen, Users, Menu, X } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { CountBadge } from "@themes/pollinator/ui/CountBadge";
import VideoUIControlsAudioInputContainer from "./VideoUIControlsAudioInputContainer";
import VideoUIControlsVideoInputContainer from "./VideoUIControlsVideoInputContainer";
import VideoUIControlsContentShareContainer from "./VideoUIControlsContentShareContainer";
import VideoUIControlsEndMeetingContainer from "./VideoUIControlsEndMeetingContainer";
import VideoUIControlsTranscriptionContainer from "./VideoUIControlsTranscriptionContainer";
import { VideoUIActiveTab } from "../../videoUI.types";
import { useContainerWidthBreakpoint } from "@core/hooks/useContainerWidthBreakpoint";
import { videoEventBus } from "../../../video/videoEventBus";
import { useChatVisibility } from "@platform/hooks/useChatVisibility";
import type { Circle } from "@core/types/core.types";

export type Props = {
  onMenuClick: (tab: VideoUIActiveTab) => void;
  closeSidebar: () => void;
  isSidebarOpen: boolean;
  unreadCount: number;
  userCircles: Circle[] | null | undefined;
};

export function VideoUIControls({
  onMenuClick,
  closeSidebar,
  isSidebarOpen,
  unreadCount = 0,
  userCircles,
}: Props) {
  const ref = createRef<HTMLDivElement>();
  const containerBreakpoints = useContainerWidthBreakpoint(ref);
  const { shouldShowChat } = useChatVisibility({ userCircles });

  const handleMenuClick = () => {
    if (isSidebarOpen) {
      closeSidebar();
    } else {
      // Default to materials if chat is disabled, otherwise chat
      onMenuClick(shouldShowChat ? "chat" : "materials");
    }
  };

  const handleRosterClick = () => {
    onMenuClick("roster");
    videoEventBus.emitEvent("videochat:participant:viewed", {});
  };

  return (
    <div ref={ref} className="w-full flex py-6 px-4">
      <div className="w-full inline-flex items-center justify-between">
        <div className="absolute left-4 bottom-6">
          <VideoUIControlsTranscriptionContainer />
        </div>
        <div className="flex flex-row flex-wrap items-start justify-center flex-1 gap-2 md:items-center md:flex-1">
          <VideoUIControlsAudioInputContainer
            containerBreakpoints={containerBreakpoints}
          />
          <VideoUIControlsVideoInputContainer
            containerBreakpoints={containerBreakpoints}
          />
          <VideoUIControlsContentShareContainer
            containerBreakpoints={containerBreakpoints}
          />
          {containerBreakpoints.isLg ? (
            <>
              {shouldShowChat && (
                <div className="relative flex items-center">
                  <Button
                    iconComponent={MessageSquare}
                    onClick={() => onMenuClick("chat")}
                    variant="ghost"
                    theme="dark"
                  >
                    {containerBreakpoints.isMd ? "Chat" : ""}
                  </Button>
                  <CountBadge count={unreadCount} theme="dark" />
                </div>
              )}
              <Button
                iconComponent={BookOpen}
                onClick={() => onMenuClick("materials")}
                variant="ghost"
                theme="dark"
              >
                {containerBreakpoints.isMd ? "Materials" : ""}
              </Button>
              <Button
                iconComponent={Users}
                onClick={handleRosterClick}
                variant="ghost"
                theme="dark"
              >
                {containerBreakpoints.isMd ? "Attendees" : ""}
              </Button>
            </>
          ) : (
            <>
              {" "}
              <div className="relative flex items-center">
                <Button
                  iconComponent={isSidebarOpen ? X : Menu}
                  onClick={handleMenuClick}
                  variant="ghost"
                  theme="dark"
                >
                  {containerBreakpoints.isMd
                    ? isSidebarOpen
                      ? "Close Menu"
                      : "Menu"
                    : ""}
                </Button>
                {!isSidebarOpen && (
                  <CountBadge count={unreadCount} theme="dark" />
                )}
              </div>
            </>
          )}
        </div>
        <div className="absolute right-4 bottom-6">
          <VideoUIControlsEndMeetingContainer />
        </div>
      </div>
    </div>
  );
}

export default VideoUIControls;
